import { useState, useEffect } from 'react';
import { Calendar as CalendarIcon, List, Activity, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import apiClient from '@/utils/apiClient';
import { toast } from 'sonner';
import useParentData from '@/hooks/useParentData';
import ChildSelector from './ChildSelector';
import CalendarView from './CalendarView';

const ActivitiesView = () => {
  const { children, selectedChild, setSelectedChild, loading } = useParentData();
  const [viewMode, setViewMode] = useState('calendar');
  const [activitiesData, setActivitiesData] = useState([]);
  const [stats, setStats] = useState({});
  const [loadingData, setLoadingData] = useState(false);

  useEffect(() => {
    if (selectedChild) {
      fetchActivities(selectedChild.id);
    }
  }, [selectedChild]);

  const fetchActivities = async (studentId) => {
    setLoadingData(true);
    try {
      const response = await apiClient.get(`/v1/parent/child/${studentId}/activities`);
      setActivitiesData(response.data.activities || []);
      setStats(response.data.stats || {});
    } catch (error) {
      toast.error('Failed to load activities');
    } finally {
      setLoadingData(false);
    }
  };

  const getTypeColor = (type) => {
    const colors = {
      sports: 'bg-blue-500',
      cultural: 'bg-purple-500',
      academic: 'bg-green-500',
      other: 'bg-gray-500'
    };
    return colors[type] || 'bg-gray-500';
  };

  const getTypeBadge = (type) => {
    const badges = {
      sports: 'bg-blue-100 text-blue-800',
      cultural: 'bg-purple-100 text-purple-800',
      academic: 'bg-green-100 text-green-800',
      other: 'bg-gray-100 text-gray-800'
    };
    return badges[type] || 'bg-gray-100 text-gray-800';
  };

  const getTypeIcon = (type) => {
    const icons = {
      sports: 'S',
      cultural: 'C',
      academic: 'A',
      other: 'O'
    };
    return icons[type] || 'O';
  };

  const renderCalendarCell = (dayData) => {
    // If multiple activities on same day, show count
    const activitiesOnDay = activitiesData.filter(a => a.date === dayData.date);
    
    if (activitiesOnDay.length > 1) {
      return (
        <div className="flex flex-col items-center justify-center h-full">
          <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center">
            <span className="text-white text-xs font-bold">{activitiesOnDay.length}</span>
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center justify-center h-full">
        <div className={`w-8 h-8 rounded-full ${getTypeColor(dayData.activity_type)} flex items-center justify-center`}>
          <span className="text-white text-xs font-bold">
            {getTypeIcon(dayData.activity_type)}
          </span>
        </div>
        <p className="text-xs text-gray-600 mt-1 truncate w-full text-center">{dayData.title || dayData.activity_name}</p>
      </div>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (loading || loadingData) {
    return <div className="p-6 text-center text-gray-500">Loading activities...</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold" style={{ color: '#2C5F7F' }}>Activities</h1>
        <p className="text-gray-600 mt-1">Track your child's activities and participation</p>
      </div>

      <ChildSelector 
        children={children}
        selectedChild={selectedChild}
        onSelectChild={setSelectedChild}
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-600">Total</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" style={{ color: '#2C5F7F' }}>
              {stats.total || 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-600">Sports</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-600">
              {stats.by_type?.sports || 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-600">Cultural</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-purple-600">
              {stats.by_type?.cultural || 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-600">Academic</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">
              {stats.by_type?.academic || 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-600">Other</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-600">
              {stats.by_type?.other || 0}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* View Toggle */}
      <div className="flex gap-2">
        <Button
          variant={viewMode === 'calendar' ? 'default' : 'outline'}
          onClick={() => setViewMode('calendar')}
          className={viewMode === 'calendar' ? 'btn-primary text-white' : ''}
        >
          <CalendarIcon className="w-4 h-4 mr-2" />
          Calendar View
        </Button>
        <Button
          variant={viewMode === 'list' ? 'default' : 'outline'}
          onClick={() => setViewMode('list')}
          className={viewMode === 'list' ? 'btn-primary text-white' : ''}
        >
          <List className="w-4 h-4 mr-2" />
          List View
        </Button>
      </div>

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <CalendarView
          data={activitiesData}
          renderCell={renderCalendarCell}
          onDateClick={(date, data) => {
            if (data) {
              const dayActivities = activitiesData.filter(a => a.date === date);
              const activityNames = dayActivities.map(a => a.title || a.activity_name).join(', ');
              toast.info(`${date}: ${activityNames}`);
            }
          }}
        />
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <Card>
          <CardHeader>
            <CardTitle>Activity Records</CardTitle>
          </CardHeader>
          <CardContent>
            {activitiesData.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No activities found</p>
            ) : (
              <div className="space-y-3">
                {activitiesData.map((activity, index) => (
                  <div key={index} className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <Activity className="w-5 h-5 text-blue-600" />
                        <div>
                          <p className="font-semibold">{activity.title || activity.activity_name}</p>
                          <p className="text-sm text-gray-500">{formatDate(activity.date)}</p>
                        </div>
                      </div>
                      <div className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${getTypeBadge(activity.activity_type)}`}>
                        {activity.activity_type}
                      </div>
                    </div>
                    {activity.description && (
                      <p className="text-sm text-gray-600 mt-2">{activity.description}</p>
                    )}
                    {activity.remarks && (
                      <div className="mt-2 text-sm">
                        <span className="text-gray-500">Remarks: </span>
                        <span className="text-gray-700">{activity.remarks}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ActivitiesView;
